import type {
  ComponentVisibility,
  FormComponent,
  FormStep,
} from '@commenda/types';
import { isComponentReadOnly, isValueEmpty } from '../utils/form-state';
import { isRuleActive } from '../utils/validation';

export const getStepTitle = (step: FormStep | undefined, index: number): string =>
  step?.title ?? `Step ${index + 1}`;

// Presence of visible required fields preview would actually enforce
// (read-only OS fields are exempt), and whether any of them are still empty —
// the empty-state check only means something when there was an empty required
// field to catch.
export function getRequiredFieldPresence(
  step: FormStep | undefined,
  formData: Record<string, unknown>,
  isFieldVisible: (visibility?: ComponentVisibility) => boolean
): { hasRequired: boolean; hasEmptyRequired: boolean } {
  let hasRequired = false;
  let hasEmptyRequired = false;
  (step?.modules ?? []).forEach((module) => {
    if (
      module.visibility &&
      !isFieldVisible(module.visibility as ComponentVisibility)
    ) {
      return;
    }

    // Repeatable modules store values per instance under their saveKey, not
    // under the component's own fieldId — read them the way computeStepPrefill
    // does, or filled instances look empty here. Zero instances contribute
    // nothing: no field is rendered, and the renderer validates only existing
    // instances.
    let repeatableInstances: Record<string, unknown>[] | null = null;
    if ((module.repeatable as { enabled?: boolean } | undefined)?.enabled) {
      const rawInstances = formData[module.saveKey || module.id];
      // Safe: Array.isArray-guarded; instance entries are the renderer's own
      // per-instance formData objects.
      repeatableInstances = Array.isArray(rawInstances)
        ? (rawInstances as Record<string, unknown>[])
        : [];
      if (repeatableInstances.length === 0) return;
    }

    (module.components ?? []).forEach((component) => {
      const { fieldId } = component;
      if (!fieldId) return;
      if (!isFieldVisible(component.visibility)) return;
      if (isComponentReadOnly(component)) return;
      if (!isRuleActive(component.validation?.rules?.required, isFieldVisible)) {
        return;
      }
      hasRequired = true;
      const hasEmptyValue = repeatableInstances
        ? repeatableInstances.some((instance) =>
            isValueEmpty(instance?.[fieldId])
          )
        : isValueEmpty(formData[fieldId]);
      if (hasEmptyValue) {
        hasEmptyRequired = true;
      }
    });
  });
  return { hasRequired, hasEmptyRequired };
}

// A validation rule's `when` condition tree shares the component-visibility
// condition shape (see ValidationRule.when in @commenda/types).
type WhenCondition = ComponentVisibility['condition'];

function collectLeafConditions(
  condition: WhenCondition | undefined,
  leafConditions: WhenCondition[]
): void {
  if (!condition || typeof condition !== 'object') return;
  if (typeof condition.field === 'string' && condition.field) {
    leafConditions.push(condition);
  }
  (condition.and ?? []).forEach((nested) =>
    collectLeafConditions(nested, leafConditions)
  );
  (condition.or ?? []).forEach((nested) =>
    collectLeafConditions(nested, leafConditions)
  );
}

// commendaOS mode as authored, whichever of the two DSL homes it lives in
// (mirrors isComponentReadOnly's lookup).
function getCommendaOSMode(component: FormComponent): string | undefined {
  const dataSourceMode = component.dataSource?.commendaOS?.mode;
  if (typeof dataSourceMode === 'string') return dataSourceMode;
  const propsMode = component.props?.mode;
  return typeof propsMode === 'string' ? propsMode : undefined;
}

// Authoring bugs in required rules that runtime behavior hides, surfaceable
// only by scanning the DSL:
// - `when` references a field that doesn't exist → the rule never activates,
//   the field is silently optional (no asterisk, no error).
// - `when` needs a static-options choice field to equal a value that isn't
//   among its options → same silent-optional outcome.
// - required on a read-only field → preview exempts it, but production
//   enforces it against a field users can't type into (dead-end when the
//   corporation lacks the value).
export function findBrokenRequiredRules(
  step: FormStep | undefined,
  componentMap: Record<string, FormComponent> | undefined
): string[] {
  if (!componentMap) return [];
  const findings: string[] = [];
  (step?.modules ?? []).forEach((module) => {
    (module.components ?? []).forEach((component) => {
      const requiredRule = component.validation?.rules?.required;
      const isRequiredAuthored =
        requiredRule === true ||
        (typeof requiredRule === 'object' &&
          requiredRule !== null &&
          requiredRule.enabled !== false);

      // Required + read-only is a production dead-end: real users can't type
      // into the field, so submission blocks whenever Commenda OS lacks the
      // value. Preview exempts read-only fields from required, hiding exactly
      // this failure — only a DSL-level scan can surface it.
      if (isRequiredAuthored && isComponentReadOnly(component)) {
        const readOnlyLabel =
          component.label ?? component.fieldId ?? component.id;
        if (getCommendaOSMode(component) === 'read-only') {
          // 'read-only' is not a valid mode ('read' | 'read-write'): wrappers
          // only honor 'read', so the field renders EDITABLE — but OS pre-fill
          // ignores it too, so no value ever arrives on its own. Flag the
          // invalid value itself instead of claiming a dead-end that isn't one.
          findings.push(
            `"${readOnlyLabel}" uses commendaOS mode "read-only", which isn't a valid mode (use "read" or "read-write") — the field renders editable but gets no Commenda OS pre-fill.`
          );
        } else {
          findings.push(
            `"${readOnlyLabel}" is required but read-only — users can't type into it, so they get stuck whenever the corporation doesn't already have this value.`
          );
        }
      }

      if (typeof requiredRule !== 'object' || requiredRule === null) return;
      if (!('when' in requiredRule) || !requiredRule.when) return;
      const leafConditions: WhenCondition[] = [];
      collectLeafConditions(requiredRule.when, leafConditions);
      const label = component.label ?? component.fieldId ?? component.id;

      leafConditions.forEach((leafCondition) => {
        const referencedFieldId = leafCondition.field;
        if (!referencedFieldId) return;
        const referencedComponent = componentMap[referencedFieldId];

        if (!referencedComponent) {
          findings.push(
            `"${label}" is marked required, but its condition points to a field ("${referencedFieldId}") that doesn't exist in this form. The requirement never turns on — users can skip this field.`
          );
          return;
        }

        // equals-a-value-that-no-option-provides on a static choice field can
        // never be true. Free-text fields are excluded: a user could type the
        // value, so it isn't statically impossible.
        const referencedOptions = referencedComponent.options;
        if (
          leafCondition.operator === 'equals' &&
          Array.isArray(referencedOptions) &&
          referencedOptions.length > 0 &&
          !referencedOptions.some(
            (option) => option.value === leafCondition.value
          )
        ) {
          const referencedLabel =
            referencedComponent.label ?? referencedFieldId;
          findings.push(
            `"${label}" is marked required only when "${referencedLabel}" is "${String(leafCondition.value)}" — but "${referencedLabel}" has no such option. The requirement never turns on — users can skip this field.`
          );
        }
      });
    });
  });
  return findings;
}

// Human-readable label for a failing field id, so the report can say
// `the "Full name" field` instead of exposing the internal fieldId. Repeatable
// instances produce prefixed ids (saveKey.0.fieldId), hence the suffix match.
export function findFieldLabelInStep(
  step: FormStep | undefined,
  fieldId: string
): string | undefined {
  let label: string | undefined;
  (step?.modules ?? []).forEach((module) => {
    (module.components ?? []).forEach((component) => {
      if (label || !component.fieldId) return;
      if (
        component.fieldId === fieldId ||
        fieldId.endsWith(`.${component.fieldId}`)
      ) {
        label = component.label ?? component.fieldId;
      }
    });
  });
  return label;
}

